import React, { useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, IconButton, Grid, Paper
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';

interface UploadDocumentModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (files: File[]) => void;
}

const ACCEPTED_TYPES = '.jpg,.jpeg,.png,.pdf';
const MAX_SIZE_MB = 5;

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({ open, onClose, onUpload }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addFiles = (filesArr: File[]) => {
    const validFiles: File[] = [];
    for (const file of filesArr) {
      if (!ACCEPTED_TYPES.includes(file.name.split('.').pop()?.toLowerCase() || '')) {
        setError('Unsupported file type.');
        continue;
      }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        setError('File size exceeds 5MB.');
        continue;
      }
      validFiles.push(file);
    }
    setSelectedFiles(prev => [...prev, ...validFiles]);
    setError(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArr = Array.from(e.target.files);
      addFiles(filesArr);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleUpload = () => {
    if (selectedFiles.length > 0) {
      onUpload(selectedFiles);
      setSelectedFiles([]);
      onClose();
    }
  };

  const handleDelete = (idx: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Document</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            border: dragActive ? '2px solid #1976d2' : '2px dashed #bdbdbd',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            background: dragActive ? '#f0f7ff' : '#fafafa',
            cursor: 'pointer',
            transition: 'border 0.2s, background 0.2s',
            mb: 2,
          }}
          onClick={handleClickUpload}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>Drag and Drop or Click to Upload</Typography>
          <Typography variant="body2" color="textSecondary">
            Supported formats: JPG, JPEG, PNG, PDF. Max size: 5MB each.
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            hidden
            onChange={handleFileChange}
          />
        </Box>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        )}
        {selectedFiles.length > 0 && (
          <Grid container spacing={2}>
            {selectedFiles.map((file, idx) => (
              <Grid item xs={12} key={idx}>
                <Paper variant="outlined" sx={{ p: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" noWrap sx={{ maxWidth: '80%' }}>{file.name}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(idx)}
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleUpload} variant="contained" color="primary" disabled={selectedFiles.length === 0}>
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDocumentModal;
